import React, { Dispatch, RefObject } from 'react';
import { Draggable } from '@hello-pangea/dnd';
import { CategoryType } from 'src/graphql';
import { HumanReadableLanguages } from 'src/types';
import { useTheme } from 'styled-components';
import { Category } from './Category';
import {
  CategoryContainer,
  CategoryJoiner,
  CategoryListItem,
  OrderContainer,
  OrderNumber,
} from './Category.styles';

interface Props {
  category: CategoryType;
  currentLocale: keyof typeof HumanReadableLanguages;
  setCurrentCategory: React.Dispatch<React.SetStateAction<CategoryType | null>>;
  currentCategory: CategoryType | null;
  isCurrentCategory: boolean;
  isCurrentCategoryChanged: boolean;
  unnamedCategoryCount: number;
  updateCategory: () => void;
  setUpdateCategoryModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  newCategory: number | null;
  newCategoryRef?: RefObject<HTMLDivElement | null>;
  index: number;
  isDragDisabled: boolean;
  isDragging?: boolean;
  onSaveTitle: (id: number, name: string, setName: Dispatch<string>) => void;
  onClickCategory: () => void;
  categoryJoinerCoordinates: (number[] | null[])[];
  setRef: (element: HTMLElement | null, key: string) => void;
  recalculateCoordinates: () => void;
}

export const CategoryAndOrder = ({
  category,
  index,
  isDragDisabled,
  currentLocale,
  setCurrentCategory,
  currentCategory,
  isCurrentCategory,
  unnamedCategoryCount,
  updateCategory,
  setUpdateCategoryModalOpen,
  newCategory,
  newCategoryRef,
  isCurrentCategoryChanged,
  onSaveTitle,
  onClickCategory,
  categoryJoinerCoordinates,
  setRef,
  recalculateCoordinates,
}: Props) => {
  const theme = useTheme();
  const [x1, x2, y1, y2] = categoryJoinerCoordinates?.[index] ?? [
    null,
    null,
    null,
    null,
  ];
  const coordinatesDefined = x1 && x2 && y1 && y2;

  return (
    <Draggable
      isDragDisabled={isDragDisabled}
      key={category.id}
      draggableId={category.id.toString()}
      index={index}
    >
      {(provided) => (
        <CategoryListItem
          row={index}
          {...provided.dragHandleProps}
          {...provided.draggableProps}
          id={`category-item-${category.id}`}
          data-testid={`category-item-${category.id}`}
        >
          <CategoryContainer ref={provided.innerRef}>
            <OrderContainer>
              <OrderNumber
                key={`${category.id.toString()}-priority`}
                ref={(element) =>
                  setRef(element, `${category.id.toString()}-priority`)
                }
                {...provided.dragHandleProps}
              >
                {index + 1}
              </OrderNumber>
              {coordinatesDefined && (
                <CategoryJoiner
                  height="100%"
                  width="100px"
                  key={`${x1}${x2}${y1}${y2}`}
                >
                  <line
                    x1={x1}
                    x2={x2}
                    y1={y1}
                    y2={y2}
                    stroke={
                      theme.isVaultVariant
                        ? theme.newPalette.turquoise.T300
                        : theme.atlas.semantic.color.action.primary.default
                    }
                    strokeWidth="1"
                  />
                </CategoryJoiner>
              )}
            </OrderContainer>
            <Category
              category={category}
              currentLocale={currentLocale}
              setCurrentCategory={setCurrentCategory}
              currentCategory={currentCategory}
              isCurrentCategory={isCurrentCategory}
              unnamedCategoryCount={unnamedCategoryCount}
              updateCategory={updateCategory}
              setUpdateCategoryModalOpen={setUpdateCategoryModalOpen}
              newCategory={newCategory}
              isCurrentCategoryChanged={isCurrentCategoryChanged}
              newCategoryRef={newCategoryRef}
              onSaveTitle={onSaveTitle}
              onClickCategory={onClickCategory}
              recalculateCoordinates={recalculateCoordinates}
            />
          </CategoryContainer>
        </CategoryListItem>
      )}
    </Draggable>
  );
};
